import { IDatabaseColumnModel } from "@kuika/kuika-cl-model";
import classNames from "classnames";
import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import GenerateWithAiPassiveSvg from "../../../svgs/generate-with-ai-passive.svg";
import KuikaCheckbox from "../../ui-kit/kuika-checkbox/kuika-checkbox";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import KuikaOverflowText from "../../ui-kit/kuika-overflow-text/kuika-overflow-text";
import { OVERFLOW_REVEAL_HOST_CLASS } from "../../ui-kit/kuika-overflow-text/overflow-reveal-controller";
import KuikaPopover from "../../ui-kit/kuika-popover/kuika-popover";
import KuikaSmallSpinner from "../../ui-kit/kuika-small-spinner/kuika-small-spinner";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import KuikaTooltip from "../../ui-kit/kuika-tooltip/kuika-tooltip";
import {
  IDataTypePanelCategory,
  IDataTypePanelOption,
  KuikaDataTypePanel
} from "../shared/kuika-data-type-dropdown/kuika-data-type-panel";
import { ColumnErrorMap, ColumnField, IColumnRowErrors } from "./table-column-errors";
import { isMaxColumnSize } from "./table-column-types";

/**
 * The columns editor: one row per column, every cell an editor, and a trailing draft row that
 * becomes a column once it has a name and a type. Same anatomy as the workflow's Process
 * Parameters grid.
 *
 * It follows that grid on the point that matters: **an edit stays local**. A cell writes its
 * row and nothing else, and the table is persisted once, by the host's CREATE. Routing every
 * cell through the add path instead re-sorted the grid under the caret, reset the fields and
 * called the backend on each keystroke.
 *
 * Deliberately NOT a `KuikaTable`: every cell is a control, so this is a form laid out in
 * columns rather than a data table with sorting and paging.
 */

/** What the draft row hands back when it becomes a column. */
export interface IColumnRowValues {
  colname: string;
  /** The serialised `IColumnTypes` the type options carry as their value. */
  coltype: string;
  defaultSize?: number | null;
  decimalScale?: number | null;
  required: boolean;
  unique: boolean;
  defaultValue?: string | number | null;
}

/** What the host proposes for a column name — the AI's answer, or the name-based guess. */
export interface IProposedColumnType {
  coltype: string;
  defaultSize?: number | null;
}

/** The host owns the rows, so a cell reports its edit rather than applying it. */
export interface IColumnEditHandlers {
  /** Returns why the rename was refused, or null when it was taken; a refused one keeps the cell open on it. */
  onName: (value: string, column: IDatabaseColumnModel) => string | null;
  onType: (coltype: string, column: IDatabaseColumnModel) => void;
  onSize: (value: number | null, column: IDatabaseColumnModel) => void;
  onScale: (value: number | null, column: IDatabaseColumnModel) => void;
  onRequired: (checked: boolean, column: IDatabaseColumnModel) => void;
  onUnique: (checked: boolean, column: IDatabaseColumnModel) => void;
  onDefault: (value: string | number | null, column: IDatabaseColumnModel) => void;
}

export interface ITableColumnsGridProps {
  columns: IDatabaseColumnModel[];
  /** The Type cell's picker, one tab per category (the primitives, then one per schema). */
  typeCategories: IDataTypePanelCategory[];
  /** PK / FK chips for a column name; the host already resolves them from the schema. */
  renderKeys: (columnName: string) => ReactNode;
  /** The icon + name the host draws for a column's type. */
  renderTypeLabel: (column: IDatabaseColumnModel) => ReactNode;
  /** The "→ table.column" tail an outgoing foreign key adds after the name. */
  renderNameSuffix: (columnName: string) => ReactNode;
  /**
   * The Default cell: what a default may be depends on the type, which the host knows. The
   * draft row draws one too, so where the value goes is the caller's to say.
   */
  renderDefault: (column: IDatabaseColumnModel, onChange: (value: string | number | null) => void) => ReactNode;
  /** Which type option a stored column is on; the host owns the schema-name matching. */
  resolveTypeValue: (column: IDatabaseColumnModel) => string;
  edit: IColumnEditHandlers;
  /**
   * The draft row, once it has both a name and a type. Returns why it was refused, or null when it
   * was taken — a refused row stays in the draft, with what was typed, rather than being thrown away.
   */
  onAddColumn: (values: IColumnRowValues) => string | null;
  onDelete: (column: IDatabaseColumnModel) => void;
  /**
   * Whether the draft row has been started but has not landed. It lives here, so without this
   * the host's save cannot see it and would drop what was typed without a word.
   */
  onDraftPending?: (pending: boolean) => void;
  /** What a column of this type falls back to the moment it stops being nullable. */
  defaultForType: (kuikaTypeName: string) => string | null;
  /** What the typed name suggests; `hasType` keeps a proposal off a row you already typed. */
  onProposeType?: (colname: string, hasType: boolean) => Promise<IProposedColumnType | null>;
  /** The default Kuika table is read-only: it renders its rows and offers no editing. */
  readOnly?: boolean;
  /** "Generate with AI" sits in the type cell, where the type is chosen. */
  aiAvailable?: boolean;
  aiActive?: boolean;
  aiBusy?: boolean;
  onToggleAi?: () => void;
  /** What is wrong with each row, by the row's object: the row is outlined, a refused cell painted. */
  errors?: ColumnErrorMap;
  /** Why the draft row cannot land yet — it is outlined as a whole. */
  draftError?: string;
  /** Bumped when errors are raised: the first marked row is scrolled to and the caret put in it. */
  errorFocusRequest?: number;
}

const EMPTY_DRAFT: IColumnRowValues = {
  colname: "",
  coltype: "",
  required: false,
  unique: false
};

/** The primary key row is the one row neither editable nor removable. */
const isLockedColumn = (column: IDatabaseColumnModel): boolean => column.name === "Id";

/** Which types carry a length, and which carry a decimal scale — the card showed each only there. */
const SIZED_TYPES: string[] = ["String", "Binary"];
const SCALED_TYPES: string[] = ["Decimal"];

/** `maxLength` reaches here as a number from the picker and as a string from the schema. */
const toNumber = (value: unknown): number | null => {
  if (value == null || value === "") return null;
  const parsed: number = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

/** Size reads "Max" for the types whose length is unbounded, which the model stores as -1. */
const sizeLabel = (column: IDatabaseColumnModel): string => {
  if (isMaxColumnSize(column)) return "Max";
  const size: number | null = toNumber(column.maxLength);
  if (!size) return "";
  return String(size);
};

/** The chosen type's Kuika name; the option's value is a serialised `IColumnTypes`. */
const draftKuikaTypeName = (coltype: string): string => {
  if (!coltype) return "";
  try {
    return (JSON.parse(coltype) as { kuikaTypeName?: string }).kuikaTypeName ?? "";
  } catch {
    return "";
  }
};

/** The length a type starts on: Binary is varbinary(MAX), String the card's 400, others none. */
const startingSize = (kuikaTypeName: string): number | null => {
  if (kuikaTypeName === "Binary") return -1;
  if (kuikaTypeName === "String") return 400;
  return null;
};

/** The option a value is, whichever tab it sits on. */
const findTypeOption = (categories: IDataTypePanelCategory[], value: string): IDataTypePanelOption | undefined => {
  for (const category of categories) {
    const option: IDataTypePanelOption | undefined = category.options.find(
      (candidate: IDataTypePanelOption): boolean => candidate.value === value
    );
    if (option) return option;
  }
  return undefined;
};

interface ITypeCellProps {
  value: string;
  categories: IDataTypePanelCategory[];
  placeholder: string;
  disabled?: boolean;
  /** What the trigger shows instead of the chosen option — the type icon plus its name. */
  valueContent?: ReactNode;
  onSelect: (value: string) => void;
  trailing?: ReactNode;
  /** Lets a stored row's trigger carry its own id — the draft is the only user of the default. */
  testId?: string;
}

/**
 * A cell that opens the type picker under itself, the way the workflow grid's combos do: the same
 * searchable panel, here with one tab per schema, since a project's tables are types too.
 */
const TypeCell = ({
  value,
  categories,
  placeholder,
  disabled,
  valueContent,
  onSelect,
  trailing,
  testId = "columnTypeCell"
}: ITypeCellProps): JSX.Element => {
  const [visible, setVisible] = useState<boolean>(false);
  const selected: IDataTypePanelOption | undefined = useMemo(
    (): IDataTypePanelOption | undefined => findTypeOption(categories, value),
    [categories, value]
  );

  const renderValue = (): ReactNode => {
    if (valueContent) return valueContent;
    // The chosen option, as the list draws it — the E2E suite reads a picked type back from here.
    if (selected) {
      return (
        <span data-testid="columnTypeOption" className="kuika__data-diagram__type-option">
          {selected.icon}
          <span className="kuika__data-diagram__type-option__label" title={selected.label}>
            {selected.label}
          </span>
        </span>
      );
    }
    return placeholder;
  };

  return (
    <>
      <KuikaPopover
        visible={visible && !disabled}
        onVisibleChange={setVisible}
        align="start"
        hideCloseIcon
        className="kuika-create-table__grid-popover"
        content={
          <KuikaDataTypePanel
            categories={categories}
            value={value}
            onSelect={(next: string): void => onSelect(next)}
            onRequestClose={(): void => setVisible(false)}
            className="kuika-create-table__type-picker"
            testId="columnTypePicker"
            searchTestId="columnTypeSearchInput"
            tabsTestId="columnTypeTabs"
          />
        }
      >
        <button
          type="button"
          disabled={disabled}
          data-testid={testId}
          className={classNames("kuika-create-table__grid__combo", {
            "kuika-create-table__grid__combo--empty": !selected && !valueContent
          })}
        >
          <span className="kuika-create-table__grid__combo-value">{renderValue()}</span>
          {!disabled && (
            <KuikaSvg name={visible ? "chevron-up" : "chevron-down"} type="arrows" width={12} height={12} />
          )}
        </button>
      </KuikaPopover>
      {trailing}
    </>
  );
};

interface IGridCellProps {
  className?: string;
  /** Why the cell's value was refused; the cell is painted and says it on hover and focus. */
  error?: string;
  children?: ReactNode;
}

/**
 * A cell whose value can be refused. The tooltip stays mounted whether or not there is an error:
 * the bare-child fallback would change the element type as an error comes and goes, and React
 * would unmount the field under the caret. The message is also in the cell as text, for a
 * screen reader walking the row.
 */
const GridCell = ({ className, error, children }: IGridCellProps): JSX.Element => (
  // Held shut while there is nothing to say: an uncontrolled root would still "open" on hover,
  // re-render the cell for nothing and warm the shared delay for the next real tooltip.
  <KuikaTooltip content={error} bgcolor="red" keepMounted open={error ? undefined : false}>
    <div
      className={classNames("kuika-create-table__grid__cell", className, {
        "kuika-create-table__grid__cell--error": !!error
      })}
      aria-invalid={error ? true : undefined}
    >
      {children}
      {error && <span className="kuika-visually-hidden">{error}</span>}
    </div>
  </KuikaTooltip>
);

/** Ids for a row's error text, which the row points at; React 17 has no `useId`. */
let rowErrorIdSeed: number = 0;

const nextRowErrorId = (): string => {
  rowErrorIdSeed += 1;
  return `kuika-create-table-row-error-${rowErrorIdSeed}`;
};

/** One id per row for as long as the row is mounted. */
const useRowErrorId = (): string => {
  const [id] = useState<string>(nextRowErrorId);
  return id;
};

interface IRowErrorMarkProps {
  id: string;
  message: string;
}

/** The glyph at the start of a refused row: the message on hover or focus, and as the row's description. */
const RowErrorMark = ({ id, message }: IRowErrorMarkProps): JSX.Element => (
  <>
    <KuikaTooltip content={message} bgcolor="red" position="top">
      <button
        type="button"
        className="kuika-create-table__grid__row-error"
        aria-label="Row error"
        aria-describedby={id}
      >
        <KuikaSvg name="alert-circle" width={14} height={14} color="var(--red-5)" />
      </button>
    </KuikaTooltip>
    <span id={id} className="kuika-visually-hidden">
      {message}
    </span>
  </>
);

/** The classes and the description a row takes, refused or not. */
interface IRowErrorAttributes {
  className: string;
  "aria-invalid"?: boolean;
  "aria-describedby"?: string;
}

const rowErrorAttributes = (error: string | undefined, id: string, ...classes: string[]): IRowErrorAttributes => ({
  className: classNames("kuika-create-table__grid__row", ...classes, {
    "kuika-create-table__grid__row--error": !!error
  }),
  "aria-invalid": error ? true : undefined,
  "aria-describedby": error ? id : undefined
});

interface INumberCellProps {
  value: number | null;
  /** String and Binary can be unbounded; the model stores that as -1 and the card called it "Max". */
  allowMax?: boolean;
  testId: string;
  onCommit: (value: number | null) => void;
}

/**
 * A length or a scale, typed in the cell. `Max` is a toggle rather than the card's Custom/Max
 * dropdown: in a cell a second combo costs more room than the one value it chooses between.
 */
const NumberCell = ({ value, allowMax = false, testId, onCommit }: INumberCellProps): JSX.Element => {
  const isMax: boolean = value === -1;
  const [draft, setDraft] = useState<string>(isMax || value == null ? "" : String(value));

  // The row is the record; a value the host did not take must not linger in the cell.
  useEffect((): void => setDraft(value === -1 || value == null ? "" : String(value)), [value]);

  const commit = (): void => {
    const trimmed: string = draft.trim();
    const next: number | null = trimmed === "" ? null : Number(trimmed);
    if (next != null && Number.isNaN(next)) {
      setDraft(value == null ? "" : String(value));
      return;
    }
    if (next === value) return;
    onCommit(next);
  };

  return (
    <>
      {/* Max is not a number — it is the absence of a limit, which the model stores as -1. An
          empty disabled box read as "no value"; the word says which of the two states it is in. */}
      {isMax ? (
        <span className="kuika-create-table__grid__max-value">Max</span>
      ) : (
        <KuikaInput
          borderless
          type="number"
          min={1}
          value={draft}
          data-testid={testId}
          onChange={(_e: unknown, next: string | number): void => setDraft(next == null ? "" : String(next))}
          onBlur={commit}
          onPressEnter={commit}
        />
      )}
      {allowMax && (
        <button
          type="button"
          aria-pressed={isMax}
          data-testid={`${testId}Max`}
          className={classNames("kuika-create-table__grid__max", {
            "kuika-create-table__grid__max--on": isMax
          })}
          // Leaving Max puts the row back on the size the card defaulted to.
          onClick={(): void => onCommit(isMax ? 400 : -1)}
        >
          MAX
        </button>
      )}
    </>
  );
};

interface IStoredRowProps {
  column: IDatabaseColumnModel;
  typeCategories: IDataTypePanelCategory[];
  typeValue: string;
  editable: boolean;
  keys: ReactNode;
  nameSuffix: ReactNode;
  typeLabel: ReactNode;
  defaultCell: ReactNode;
  edit: IColumnEditHandlers;
  onDelete: () => void;
  errors?: IColumnRowErrors;
}

/**
 * A column already on the table. Its cells are the same controls the draft row uses, so a
 * correction is made where the value is read rather than in a separate editor above the grid.
 */
const StoredRow = ({
  column,
  typeCategories,
  typeValue,
  editable,
  keys,
  nameSuffix,
  typeLabel,
  defaultCell,
  edit,
  onDelete,
  errors
}: IStoredRowProps): JSX.Element => {
  const [name, setName] = useState<string>(column.name);
  // A refused name stays in the cell, marked with why, so it can be corrected rather than retyped.
  const [refusal, setRefusal] = useState<string | null>(null);
  /**
   * The name is a label until you go to change it. A field cannot do what every other clipped
   * name in the designer does — fade and slide its tail into view on hover — because the reveal
   * moves a track element and a field renders its text natively. Showing the label at rest buys
   * that behaviour; the field appears on the click that starts the edit, and a refused name keeps
   * it open so the correction happens where the mistake is.
   */
  const [editing, setEditing] = useState<boolean>(false);
  const rowErrorId: string = useRowErrorId();

  useEffect((): void => {
    setName(column.name);
    setRefusal(null);
  }, [column.name]);

  const sized: boolean = SIZED_TYPES.includes(column.kuikaDataType);
  const scaled: boolean = SCALED_TYPES.includes(column.kuikaDataType);
  const fieldError = (field: ColumnField): string | undefined => errors?.fields?.[field];
  // What the host says about the name is about the SAVED name: once another is being typed it no
  // longer applies, and the cell stops saying it.
  const hostNameError: string | undefined = editing && name !== column.name ? undefined : fieldError("name");
  const nameError: string | undefined = refusal ?? hostNameError;

  const commitName = (): void => {
    const trimmed: string = name.trim();
    if (!trimmed || trimmed === column.name) {
      setName(column.name);
      setRefusal(null);
      setEditing(false);
      return;
    }
    const refused: string | null = edit.onName(trimmed, column);
    setRefusal(refused);
    // A refused name keeps the field open, on the value that was refused, so the correction
    // happens where the mistake is rather than after a second click.
    setEditing(refused !== null);
  };

  const startEditing = (): void => {
    if (editable) {
      setEditing(true);
    }
  };

  return (
    // Hosts the reveal so a clipped name slides while the ROW is hovered, not only the label —
    // the same wiring every other list in the designer uses.
    <div
      role="row"
      data-testid={`${column.name}ColumnRow`}
      {...rowErrorAttributes(errors?.row, rowErrorId, OVERFLOW_REVEAL_HOST_CLASS)}
    >
      {/* The badges are the whole cell's content, so they sit on its centre line — the same
          treatment Required and Unique already get. */}
      <div className="kuika-create-table__grid__cell kuika-create-table__grid__cell--center">
        {errors?.row && <RowErrorMark id={rowErrorId} message={errors.row} />}
        {keys}
      </div>
      <GridCell className={classNames({ "kuika-create-table__grid__cell--flush": editable })} error={nameError}>
        {/* The name and the FK tail share a box of their own so the cell can stay their query
            container: a container query styles what is inside a container, never the container
            itself, and the two need to turn into a column when the track gets short. */}
        <div className="kuika-create-table__grid__name-cell">
          {editable && editing ? (
            <KuikaInput
              borderless
              autoFocus
              value={name}
              title={name}
              data-testid={`${column.name}ColumnNameCell`}
              onChange={(_e: unknown, value: string | number): void => {
                setName(String(value ?? ""));
                setRefusal(null);
              }}
              onBlur={commitName}
              onPressEnter={commitName}
            />
          ) : (
            <span
              className={classNames("kuika-create-table__grid__text", {
                "kuika-create-table__grid__text--editable": editable
              })}
              title={name}
              data-testid={`${column.name}ColumnNameCell`}
              role={editable ? "button" : undefined}
              tabIndex={editable ? 0 : undefined}
              onClick={startEditing}
              onFocus={startEditing}
            >
              <KuikaOverflowText>{name}</KuikaOverflowText>
            </span>
          )}
          {nameSuffix}
        </div>
      </GridCell>
      <GridCell
        className={classNames({ "kuika-create-table__grid__cell--flush": editable })}
        error={fieldError("type")}
      >
        {editable ? (
          <TypeCell
            value={typeValue}
            categories={typeCategories}
            placeholder="Choose type.."
            valueContent={typeLabel}
            testId={`${column.name}ColumnTypeCell`}
            onSelect={(value: string): void => edit.onType(value, column)}
          />
        ) : (
          typeLabel
        )}
      </GridCell>
      <GridCell
        className={classNames({ "kuika-create-table__grid__cell--flush": editable && sized })}
        error={fieldError("size")}
      >
        {editable && sized ? (
          <NumberCell
            // Display only: an Oracle Max column reads back as 2000, so the cell is fed the
            // sentinel the picker writes rather than the length the schema returned.
            value={isMaxColumnSize(column) ? -1 : toNumber(column.maxLength)}
            allowMax
            testId={`${column.name}ColumnSize`}
            onCommit={(next: number | null): void => edit.onSize(next, column)}
          />
        ) : (
          sizeLabel(column)
        )}
      </GridCell>
      <GridCell
        className={classNames({ "kuika-create-table__grid__cell--flush": editable && scaled })}
        error={fieldError("scale")}
      >
        {editable && scaled ? (
          <NumberCell
            value={toNumber(column.decimalScale)}
            testId={`${column.name}ColumnScale`}
            onCommit={(next: number | null): void => edit.onScale(next, column)}
          />
        ) : (
          (column.decimalScale ?? "")
        )}
      </GridCell>
      <div className="kuika-create-table__grid__cell kuika-create-table__grid__cell--center">
        <KuikaCheckbox
          checked={!column.nullable}
          disabled={!editable}
          dataTestId={`${column.name}ColumnRequired`}
          onChange={(e: React.ChangeEvent<HTMLInputElement>): void => edit.onRequired(e.target.checked, column)}
        />
      </div>
      <div className="kuika-create-table__grid__cell kuika-create-table__grid__cell--center">
        <KuikaCheckbox
          checked={!!column.isUnique}
          disabled={!editable}
          dataTestId={`${column.name}ColumnUnique`}
          onChange={(e: React.ChangeEvent<HTMLInputElement>): void => edit.onUnique(e.target.checked, column)}
        />
      </div>
      <GridCell
        className={classNames({ "kuika-create-table__grid__cell--flush": editable })}
        error={fieldError("default")}
      >
        {editable ? defaultCell : (column.defaultValue ?? "")}
      </GridCell>
      <div className="kuika-create-table__grid__cell kuika-create-table__grid__cell--center">
        {editable && (
          <KuikaTooltip content="Delete column" position="top">
            <button
              type="button"
              className="kuika-create-table__grid__remove"
              aria-label={`Delete ${column.name}`}
              data-testid={`${column.name}ColumnDeleteButton`}
              onClick={onDelete}
            >
              <KuikaSvg name="trash" width={14} height={14} color="var(--red-5)" />
            </button>
          </KuikaTooltip>
        )}
      </div>
    </div>
  );
};

/** Scrolls the first marked row into view and puts the caret where the correction is made. */
const focusFirstError = (root: HTMLElement): void => {
  const first: HTMLElement | null = root.querySelector<HTMLElement>(
    ".kuika-create-table__grid__row--error, .kuika-create-table__grid__cell--error"
  );
  if (!first) return;
  // jsdom has no layout, so no scrollIntoView.
  if (typeof first.scrollIntoView === "function") first.scrollIntoView({ block: "nearest" });
  // A refused row is explained by its glyph; a refused cell is corrected in its control. A name at
  // rest is a label that opens its field when focused.
  const target: HTMLElement | null = first.classList.contains("kuika-create-table__grid__row--error")
    ? first.querySelector<HTMLElement>(".kuika-create-table__grid__row-error")
    : first.querySelector<HTMLElement>("input, button, [tabindex]");
  target?.focus();
};

const TableColumnsGrid = ({
  columns,
  typeCategories,
  renderKeys,
  renderTypeLabel,
  renderNameSuffix,
  renderDefault,
  defaultForType,
  resolveTypeValue,
  edit,
  onAddColumn,
  onDelete,
  onDraftPending,
  onProposeType,
  readOnly = false,
  aiAvailable = false,
  aiActive = false,
  aiBusy = false,
  onToggleAi,
  errors,
  draftError,
  errorFocusRequest = 0
}: ITableColumnsGridProps): JSX.Element => {
  const [draft, setDraft] = useState<IColumnRowValues>(EMPTY_DRAFT);
  // Why the host refused the draft's name; the name cell says it until the name is edited.
  const [draftNameError, setDraftNameError] = useState<string | null>(null);
  const draftRowErrorId: string = useRowErrorId();
  // Started but not landed. A draft that lands resets, so anything still in it has not: half-filled,
  // refused (a name another column has), or a type the AI proposed and nobody took yet. Counting
  // only the half-filled case let a complete-but-refused row be dropped by the save without a word.
  const draftPending: boolean = !!(draft.colname.trim() || draft.coltype);
  useEffect((): void => onDraftPending?.(draftPending), [draftPending, onDraftPending]);
  // The draft as it stands right now, for the settle that has to survive an await.
  const draftRef = useRef<IColumnRowValues>(draft);
  draftRef.current = draft;
  // One settle at a time: Enter and the blur it causes both reach settleName.
  const settlingRef = useRef<boolean>(false);
  // One turn of the AI ring when a column lands, cleared by the animation's own end event so
  // the duration lives in the stylesheet rather than in a timer here.
  const [justSettled, setJustSettled] = useState<boolean>(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // Each raise of the errors (an UPDATE that was refused) takes the reader to the first of them.
  useEffect((): void => {
    if (errorFocusRequest > 0 && rootRef.current) focusFirstError(rootRef.current);
  }, [errorFocusRequest]);

  /**
   * The next column starts where the last one did. Without this the caret is left wherever the
   * row was completed — in the type popover, most of the time — and adding a run of columns
   * means reaching for the mouse between every one.
   *
   * Claimed over several frames rather than once: the popover hands focus back to its own
   * trigger as it unmounts, and that lands after the first attempt. Re-asserting for ~4 frames
   * outlasts it and then stops, so it can never fight the user for the field.
   */
  const focusDraftName = (framesLeft: number): void => {
    const node: Element | null | undefined = rootRef.current?.querySelector('[data-testid="columnNameCell"]');
    const input: HTMLInputElement | null =
      node instanceof HTMLInputElement ? node : (node?.querySelector("input") ?? null);
    if (!input) return;
    if (document.activeElement !== input) input.focus();
    if (framesLeft > 0) requestAnimationFrame((): void => focusDraftName(framesLeft - 1));
  };
  const draftTypeName: string = draftKuikaTypeName(draft.coltype);
  const draftSized: boolean = SIZED_TYPES.includes(draftTypeName);
  const draftScaled: boolean = SCALED_TYPES.includes(draftTypeName);
  // The Default cell branches on a column; the draft is one in every way that cell reads.
  const draftColumn: IDatabaseColumnModel = {
    name: draft.colname,
    kuikaDataType: draftTypeName,
    nullable: !draft.required,
    maxLength: draft.defaultSize,
    decimalScale: draft.decimalScale,
    isUnique: draft.unique,
    defaultValue: draft.defaultValue
  } as IDatabaseColumnModel;

  /**
   * A draft becomes a column the moment it has the two things a column cannot be built without:
   * a name and a type. There is no separate add step — everything else (size, scale, required,
   * unique, default) stays editable on the row it just became, so waiting would buy nothing.
   *
   * Takes the candidate rather than reading `draft`: both callers have just computed a newer
   * one, and the state update behind them has not landed yet.
   */
  const settleDraft = (candidate: IColumnRowValues, moveFocus: boolean): void => {
    if (!candidate.colname.trim() || !candidate.coltype) {
      setDraft(candidate);
      return;
    }
    const refused: string | null = onAddColumn(candidate);
    const added: boolean = refused === null;
    setDraftNameError(refused);
    setDraft(added ? EMPTY_DRAFT : candidate);
    if (!added) return;
    setJustSettled(true);
    // Only where the caret would otherwise be stranded — on Enter, and after a choice made in
    // the type popover, which hands focus back to its own trigger. A row that settled because
    // the field was left alone stays out of it: the user has already said where they are going.
    if (moveFocus) requestAnimationFrame((): void => focusDraftName(4));
  };

  /**
   * Leaving the name asks for the proposal the card's name field ran on blur and on Enter — the
   * AI when its toggle is on, the name-based guess otherwise.
   *
   * What lands the row is a type the user actually chose. A type already in the cell when the
   * name is left means the row is finished, whichever way the field is left — that is the
   * type-first order, and waiting for anything more would just be a step that says nothing new.
   * A type that arrives from the proposal during THIS blur is a suggestion, so the row stays a
   * draft and the cell can still be opened — otherwise the guess (or the AI, which disables the
   * cell while its request is out) decided before the choice was ever offered. Enter is the
   * explicit "this row is done" and takes the suggestion with it.
   */
  const settleName = async (commit: boolean): Promise<void> => {
    const colname: string = draft.colname.trim();
    if (!colname) return;
    // Enter and the blur that follows it both land here. Without this the second one settles a
    // second time from the same draft: the column is added twice and the duplicate is refused,
    // which puts the just-added name back in the draft row, outlined red.
    if (settlingRef.current) return;
    settlingRef.current = true;

    const hadType: boolean = !!draft.coltype;
    let proposed: IProposedColumnType | null = null;
    try {
      if (onProposeType) proposed = await onProposeType(colname, hadType);
    } finally {
      settlingRef.current = false;
    }

    // Rebuilt from the draft as it is NOW, not as it was before the await. With the AI on that
    // is a network round trip, and every cell except the name and the type stays live through
    // it — a Required ticked while the request was out used to be silently undone.
    const current: IColumnRowValues = draftRef.current;
    let next: IColumnRowValues = { ...current, colname };
    if (proposed) next = { ...next, coltype: proposed.coltype, defaultSize: proposed.defaultSize ?? null };

    if (commit || hadType) settleDraft(next, commit);
    else setDraft(next);
  };

  const renderAiTrigger = (): ReactNode => {
    if (!aiAvailable) return null;
    // Kept rather than swapped out: the control stays where it is and breathes while the
    // request is out, so the cell's width and the user's eye both hold still.
    if (aiBusy) {
      return (
        <span className="kuika-create-table__grid__ai kuika-create-table__grid__ai--busy">
          <GenerateWithAiPassiveSvg />
        </span>
      );
    }

    // The asset's own frame is 32x34 with one rounded edge, made to butt against a bordered
    // select. Here the square is drawn in CSS so it can actually be square; only the sparkle
    // comes from the asset, and its purple is inverted to white on the lit gradient.
    return (
      <KuikaTooltip content="Generate with AI" position="top">
        <button
          type="button"
          aria-pressed={aiActive}
          data-testid="generateColumnTypeWithAi"
          className={classNames("kuika-create-table__grid__ai", {
            "kuika-create-table__grid__ai--active": aiActive && !justSettled,
            "kuika-create-table__grid__ai--settled": justSettled
          })}
          onAnimationEnd={(): void => setJustSettled(false)}
          onClick={onToggleAi}
        >
          <GenerateWithAiPassiveSvg />
        </button>
      </KuikaTooltip>
    );
  };

  return (
    <div className="kuika-create-table__grid" role="table" ref={rootRef}>
      <div className="kuika-create-table__grid__head" role="row">
        <div className="kuika-create-table__grid__head-cell kuika-create-table__grid__cell--center">Key</div>
        <div className="kuika-create-table__grid__head-cell">Column Name</div>
        <div className="kuika-create-table__grid__head-cell">Type</div>
        <div className="kuika-create-table__grid__head-cell">Size</div>
        <div className="kuika-create-table__grid__head-cell">Scale</div>
        <div className="kuika-create-table__grid__head-cell kuika-create-table__grid__cell--center">Required</div>
        <div className="kuika-create-table__grid__head-cell kuika-create-table__grid__cell--center">Unique</div>
        <div className="kuika-create-table__grid__head-cell">Default</div>
        <div className="kuika-create-table__grid__head-cell" />
      </div>

      {columns.map((column: IDatabaseColumnModel) => (
        <StoredRow
          key={column.id ?? column.name}
          column={column}
          typeCategories={typeCategories}
          typeValue={resolveTypeValue(column)}
          editable={!readOnly && !isLockedColumn(column)}
          keys={renderKeys(column.name)}
          nameSuffix={renderNameSuffix(column.name)}
          typeLabel={renderTypeLabel(column)}
          defaultCell={renderDefault(column, (value: string | number | null): void => edit.onDefault(value, column))}
          edit={edit}
          onDelete={(): void => onDelete(column)}
          errors={errors?.get(column)}
        />
      ))}

      {!readOnly && (
        <div role="row" data-testid="columnDraftRow" {...rowErrorAttributes(draftError, draftRowErrorId)}>
          <div className="kuika-create-table__grid__cell kuika-create-table__grid__cell--center">
            {draftError && <RowErrorMark id={draftRowErrorId} message={draftError} />}
          </div>
          <GridCell className="kuika-create-table__grid__cell--flush" error={draftNameError ?? undefined}>
            <KuikaInput
              borderless
              placeholder="Enter column name.."
              data-testid="columnNameCell"
              disabled={aiBusy}
              value={draft.colname}
              onChange={(_e: unknown, value: string | number): void => {
                setDraft({ ...draft, colname: String(value ?? "") });
                setDraftNameError(null);
              }}
              onBlur={(): void => void settleName(false)}
              onPressEnter={(): void => void settleName(true)}
            />
          </GridCell>
          <div className="kuika-create-table__grid__cell kuika-create-table__grid__cell--flush">
            <TypeCell
              value={draft.coltype}
              categories={typeCategories}
              placeholder="Choose type.."
              disabled={aiBusy}
              valueContent={aiBusy ? <KuikaSmallSpinner /> : undefined}
              trailing={renderAiTrigger()}
              onSelect={(value: string): void => {
                const typeName: string = draftKuikaTypeName(value);
                settleDraft(
                  {
                    ...draft,
                    coltype: value,
                    defaultSize: startingSize(typeName),
                    decimalScale: typeName === "Decimal" ? 5 : null,
                    defaultValue: draft.required ? defaultForType(typeName) : null
                  },
                  true
                );
                // Whether that finished the row or only gave it a type, the next thing to say is
                // a name — and the popover would otherwise leave the caret on its own trigger.
                requestAnimationFrame((): void => focusDraftName(4));
              }}
            />
          </div>
          <div
            className={classNames("kuika-create-table__grid__cell", {
              "kuika-create-table__grid__cell--flush": draftSized
            })}
          >
            {draftSized && (
              <NumberCell
                value={draft.defaultSize ?? null}
                allowMax
                testId="draftColumnSize"
                onCommit={(next: number | null): void => setDraft({ ...draft, defaultSize: next })}
              />
            )}
          </div>
          <div
            className={classNames("kuika-create-table__grid__cell", {
              "kuika-create-table__grid__cell--flush": draftScaled
            })}
          >
            {draftScaled && (
              <NumberCell
                value={draft.decimalScale ?? null}
                testId="draftColumnScale"
                onCommit={(next: number | null): void => setDraft({ ...draft, decimalScale: next })}
              />
            )}
          </div>
          <div className="kuika-create-table__grid__cell kuika-create-table__grid__cell--center">
            <KuikaCheckbox
              checked={draft.required}
              dataTestId="draftColumnRequired"
              onChange={(e: React.ChangeEvent<HTMLInputElement>): void =>
                setDraft({
                  ...draft,
                  required: e.target.checked,
                  defaultValue: e.target.checked ? defaultForType(draftTypeName) : null
                })
              }
            />
          </div>
          <div className="kuika-create-table__grid__cell kuika-create-table__grid__cell--center">
            <KuikaCheckbox
              checked={draft.unique}
              dataTestId="draftColumnUnique"
              onChange={(e: React.ChangeEvent<HTMLInputElement>): void =>
                setDraft({ ...draft, unique: e.target.checked })
              }
            />
          </div>
          <div
            className={classNames("kuika-create-table__grid__cell", {
              "kuika-create-table__grid__cell--flush": !!draftTypeName
            })}
          >
            {!!draftTypeName &&
              renderDefault(draftColumn, (value: string | number | null): void =>
                setDraft({ ...draft, defaultValue: value })
              )}
          </div>
          <div className="kuika-create-table__grid__cell" />
        </div>
      )}
    </div>
  );
};

export default TableColumnsGrid;
